<script>
import Icon from "@/components/atoms/icon";
import Titre from "@/components/atoms/titre";
import LinkTo from "@/components/atoms/link-to";
import Form from "@/components/molecules/form";
import FormGroup from "@/components/molecules/form-group";
import FormInput from "@/components/molecules/form-input";
import FormSelect from "@/components/molecules/form-select";
import FormSelectOption from "@/components/molecules/form-select/form-select-option";
import Button from "@/components/atoms/button";

export default {
  name: "CheckLad",
  components: {
    Icon,
    Titre,
    LinkTo,
    Form,
    FormGroup,
    FormInput,
    FormSelect,
    FormSelectOption,
    Button
  },
  props: {
    communes: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    isEligible: {
      type: Boolean,
      required: false
    },
    state: {
      type: String,
      required: false,
      default: null
    }
  },
  emits: [
    "toggleCountriesListPopin",
    "getDeliveryTermsContent",
    "codePostalSelected",
    "resetCommunes",
    "communeValidated",
    "communeSelected",
    "togglePlanningPopin"
  ],
  data() {
    return {
      codePostal: "",
      communeId: null,
      commune: null
    };
  },
  computed: {
    isValidCodePostal() {
      return this.codePostal.length === 5;
    },
    defaultcommune() {
      if (this.communes && this.communes.length > 0) {
        return this.communes[0];
      } else {
        return null;
      }
    }
  },
  watch: {
    defaultcommune(value) {
      if (value) {
        this.communeId = value.id;
      }
    }
  },
  methods: {
    toggleCountriesListPopin() {
      this.$emit("toggleCountriesListPopin");
    },
    getContent() {
      this.$emit("getDeliveryTermsContent");
    },
    handleCodePostal() {
      if (this.isValidCodePostal) {
        this.$emit("codePostalSelected", this.codePostal);
      } else {
        this.communeId = null;
        this.$emit("resetCommunes");
      }
    },
    communeValidated() {
      const commune = this.communes.find(
        (commune) => commune.id === this.communeId
      );
      if (commune) {
        this.$emit("communeValidated", {
          code_insee: commune.attributes.code_insee,
          code_postal: commune.attributes.code_postal
        });
        this.commune = commune;
      }
    },
    retry() {
      this.commune = null;
      this.$emit("resetCommunes");
      this.codePostal = "";
    },
    eligibleCommuneSelected(commune) {
      this.$emit("communeSelected", commune);
      this.$emit("togglePlanningPopin", "LAD");
    }
  }
};
</script>
<template>
  <div>
    <Titre
      tag="h2"
      size="md"
      variant="primary"
      class="u-text-center u-mb-md u-mx-auto u-none@sm"
      style="max-width:40rem"
    >
      Vérifiez si votre commune peut bénéficier du service de livraison
    </Titre>
    <template v-if="!isEligible && commune">
      <div class="u-flex u-flex-column u-flex-row@sm u-flex-align-center u-mb-sm">
        <Icon
          name="check-circle"
          variant="success"
          size="lg"
          class="u-mb-sm u-mb-none@sm u-mr-sm@sm"
        />
        <p class="u-font-22 u-font-19@sm u-font-500 u-color-primary@sm">
          {{ commune.attributes.nom_commune }} ({{ commune.attributes.code_postal }})
        </p>
      </div>
      <p class="u-text-center u-text-left@sm u-font-15 u-mb-lg">
        Cette ville bénéficie bien du service de livraison.
      </p>
      <div class="u-text-center u-flex@md u-flex-column@md u-flex-align-center@md">
        <Button
          size="md"
          radius="full"
          variant="primary"
          class="u-mb-sm"
          @click="eligibleCommuneSelected(commune)"
        >
          Voir le planning
        </Button>
        <br>
        <Button
          link
          class="u-mb-md"
          size="md"
          radius="full"
          outline
          variant="primary"
          @click="retry()"
        >
          Essayer une autre ville
        </Button>
        <br>

        <Button
          link
          variant="text-light"
          class="u-mb-xs"
          @click="toggleCountriesListPopin()"
        >
          Voir les zones de livraison
        </Button>
        <br>
        <Button
          link
          variant="text-light"
          size="sm"
          class="u-mb-sm"
          @click="getContent()"
        >
          En savoir plus sur la livraison
        </Button>
      </div>
    </template>

    <template v-else-if="isEligible && commune">
      <div class="u-flex u-flex-column u-flex-row@sm u-flex-align-center u-mb-sm">
        <Icon
          name="alert-circle"
          variant="secondary"
          size="lg"
          class="u-mb-sm u-mb-none@sm u-mr-sm@sm"
        />
        <p class="u-font-22 u-font-19@sm u-font-500 u-color-primary@sm">
          {{ commune.attributes.nom_commune }} ({{ commune.attributes.code_postal }})
        </p>
      </div>
      <p class="u-text-center u-text-left@sm u-font-15 u-mb-lg">
        La livraison n’est pas proposée dans cette ville.
      </p>

      <div class="u-text-center">
        <Button
          size="md"
          radius="full"
          variant="primary"
          class="u-mb-md"
          @click="retry()"
        >
          Essayer une autre ville
        </Button>
        <br>
        <Button
          link
          class="u-mb-sm"
          @click="toggleCountriesListPopin()"
        >
          Voir les zones de livraison
        </Button>
        <br>
        <Button
          link
          class="u-mb-sm"
          @click="getContent()"
        >
          Conditions de livraison
        </Button>
      </div>
    </template>

    <template v-else>
      <p class="u-none u-block@sm u-mb-sm">
        Vérifiez si votre commune peut bénéficier du service de livraison
      </p>

      <Form class="u-mb-md">
        <div
          class="u-mx-auto u-mx-none@sm"
          style="max-width:24rem"
        >
          <FormGroup
            class="u-mb-xs u-text-center"
            size="sm"
            :state="isValidCodePostal ? state : null"
          >
            <FormInput
              id="cp"
              v-model="codePostal"
              size="sm"
              maxlength="5"
              type="number"
              autocomplete="postal-code"
              @input="handleCodePostal()"
            >
              Code postal
            </FormInput>
          </FormGroup>

          <FormGroup
            class="u-mb-sm"
            size="sm"
          >
            <FormSelect
              v-model="communeId"
              title="Ville"
              default-value="Votre ville"
              size="sm"
            >
              <FormSelectOption
                v-for="c in communes"
                :key="c.id"
                :value="c.id"
              >
                {{ c.attributes.nom_commune }}
              </FormSelectOption>
            </FormSelect>
          </FormGroup>
        </div>
        <div class="u-text-center u-mb-md">
          <Button
            size="md"
            radius="full"
            variant="primary"
            :disabled="!communeId"
            @click="communeValidated()"
          >
            <span class="u-none@sm">Valider</span>
            <span class="u-none u-block@sm u-px-md">Vérifier ma ville</span>
          </Button>
        </div>
      </Form>
      <div class="u-text-center">
        <Button
          link
          class="u-mb-xs"
          @click="toggleCountriesListPopin()"
        >
          Voir les zones de livraison
        </Button>
        <br>
        <LinkTo
          to="/legal/informations-juridiques/infos-specifiques"
          variant="text-light"
          size="sm"
        >
          Conditions de livraison
        </LinkTo>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
</style>
